// Which version of the app this screen is running (stamped at build time by
// vite.config.ts), and whether the server has a newer one.

import { useEffect, useState } from 'react';
import { apiUrl } from '../data/api.ts';

export interface BuildInfo {
  version: string;
  /** Short git commit. */
  commit: string;
  /** ISO time the build was made (on GitHub Actions, minutes before the deploy). */
  builtAt: string;
}

declare const __BUILD__: BuildInfo;

export const BUILD: BuildInfo = typeof __BUILD__ !== 'undefined' ? __BUILD__ : { version: 'dev', commit: 'dev', builtAt: '' };

export interface ServerBuild {
  build?: BuildInfo;
  startedAt?: number;
}

let latest: ServerBuild | undefined;
const listeners = new Set<() => void>();

async function refresh(): Promise<void> {
  try {
    const r = await fetch(apiUrl('/api/health'), { cache: 'no-store' });
    if (!r.ok) return;
    const j = (await r.json()) as ServerBuild;
    latest = { build: j.build, startedAt: j.startedAt };
    for (const l of listeners) l();
  } catch {
    /* offline — try again later */
  }
}

let timer: ReturnType<typeof setInterval> | undefined;

/** The server's build, checked every 5 minutes. */
export function useServerBuild(): { server?: ServerBuild; stale: boolean } {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    if (!timer) {
      void refresh();
      timer = setInterval(() => void refresh(), 5 * 60_000);
    }
    return () => {
      listeners.delete(l);
    };
  }, []);
  // A newer build on the server than the one this screen loaded.
  const stale = !!latest?.build && BUILD.commit !== 'dev' && latest.build.commit !== BUILD.commit && latest.build.builtAt > BUILD.builtAt;
  return { server: latest, stale };
}
